import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";

const ConditionDropdown = ({
  material = "WOOD",
  condition = "",
  newConditionType = "",
  placeholder = "Select Condition",
  onChange = () => {},
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsSubMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getDisplayText = () => {
    if (!condition) return "";
    if (condition === "RECYCLED" || condition === "Recycled") return "Recycled";
    if (condition === "COMBO" || condition === "Combo") return "Combo";
    if (condition === "NEW" || condition === "New") {
      if (newConditionType === "HARDWOOD" || newConditionType === "New - Hardwood") {
        return "New (Hardwood)";
      }
      if (newConditionType === "SOFTWOOD" || newConditionType === "New - Softwood") {
        return "New (Softwood)";
      }
      return "New";
    }
    return condition;
  };

  const handleSelectCondition = (condVal) => {
    if (condVal === "NEW") {
      onChange({ condition: "NEW", newConditionType: "" });
    } else {
      onChange({ condition: condVal, newConditionType: "" });
    }
    setIsOpen(false);
    setIsSubMenuOpen(false);
  };

  const handleToggleSubtype = (typeVal) => {
    const isAlreadySelected = (condition === "NEW" || condition === "New") && newConditionType === typeVal;
    if (isAlreadySelected) {
      onChange({ condition: "NEW", newConditionType: "" });
    } else {
      onChange({ condition: "NEW", newConditionType: typeVal });
    }
    setIsOpen(false);
    setIsSubMenuOpen(false);
  };

  const displayText = getDisplayText();

  return (
    <Container ref={dropdownRef} className={disabled ? "disabled" : ""}>
      <ControlBox onClick={() => !disabled && setIsOpen(!isOpen)}>
        <ValueText className={!displayText ? "is-placeholder" : ""} title={displayText}>
          {displayText || placeholder}
        </ValueText>
        <ArrowIcon className={isOpen ? "open" : ""}>▼</ArrowIcon>
      </ControlBox>

      {isOpen && !disabled && (
        <Menu>
          <MenuItem
            onClick={() => handleSelectCondition("RECYCLED")}
            onMouseEnter={() => setIsSubMenuOpen(false)}
          >
            <span>Recycled</span>
          </MenuItem>

          <MenuItem
            className={material !== "PLASTIC" ? "has-submenu" : ""}
            onClick={() => handleSelectCondition("NEW")}
            onMouseEnter={() => material !== "PLASTIC" && setIsSubMenuOpen(true)}
          >
            <span>New</span>
            {material !== "PLASTIC" && <ChevronRight>›</ChevronRight>}

            {isSubMenuOpen && material !== "PLASTIC" && (
              <SubMenu onClick={(e) => e.stopPropagation()}>
                <SubMenuItem
                  onClick={() => handleToggleSubtype("HARDWOOD")}
                >
                  <CustomCheckboxBox
                    className={
                      (condition === "NEW" || condition === "New") && newConditionType === "HARDWOOD"
                        ? "checked"
                        : ""
                    }
                  >
                    {((condition === "NEW" || condition === "New") && newConditionType === "HARDWOOD") && (
                      <CheckMark>✓</CheckMark>
                    )}
                  </CustomCheckboxBox>
                  <span>New - Hardwood</span>
                </SubMenuItem>

                <SubMenuItem
                  onClick={() => handleToggleSubtype("SOFTWOOD")}
                >
                  <CustomCheckboxBox
                    className={
                      (condition === "NEW" || condition === "New") && newConditionType === "SOFTWOOD"
                        ? "checked"
                        : ""
                    }
                  >
                    {((condition === "NEW" || condition === "New") && newConditionType === "SOFTWOOD") && (
                      <CheckMark>✓</CheckMark>
                    )}
                  </CustomCheckboxBox>
                  <span>New - Softwood</span>
                </SubMenuItem>
              </SubMenu>
            )}
          </MenuItem>

          {material !== "PLASTIC" && (
            <MenuItem
              onClick={() => handleSelectCondition("COMBO")}
              onMouseEnter={() => setIsSubMenuOpen(false)}
            >
              <span>Combo</span>
            </MenuItem>
          )}
        </Menu>
      )}
    </Container>
  );
};

export default ConditionDropdown;

const Container = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;

  &.disabled > div {
    background-color: #f8fafc !important;
    border-color: #e2e8f0 !important;
    cursor: not-allowed;
  }
`;

const ControlBox = styled.div`
  border: 1px solid #d1d5db !important;
  background-color: #ffffff !important;
  border-radius: 6px;
  height: 38px;
  padding: 0 28px 0 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;

  &:hover {
    border-color: #94a3b8 !important;
  }
`;

const ValueText = styled.div`
  font-size: 13px;
  color: #1e293b;
  font-weight: 500;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: transparent !important;
  text-align: left;

  &.is-placeholder {
    color: #94a3b8 !important;
    font-weight: normal !important;
    background: transparent !important;
  }
`;

const ArrowIcon = styled.span`
  font-size: 10px;
  color: #64748b;
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  transition: transform 0.2s ease;
  pointer-events: none;

  &.open {
    transform: translateY(-50%) rotate(180deg);
  }
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 999;
  padding: 4px 0;
`;

const MenuItem = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  font-size: 13px;
  color: #334155;
  cursor: pointer;
  user-select: none;
  &:hover {
    background-color: #f1f5f9;
  }
`;

const ChevronRight = styled.span`
  font-size: 14px;
  color: #64748b;
  margin-left: 8px;
`;

const SubMenu = styled.div`
  position: absolute;
  top: 0;
  left: 100%;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  padding: 4px 0;
  min-width: 150px;
`;

const SubMenuItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  font-size: 13px;
  color: #334155;
  cursor: pointer;
  user-select: none;
  &:hover {
    background-color: #f1f5f9;
  }
`;

const CustomCheckboxBox = styled.div`
  width: 16px;
  height: 16px;
  min-width: 16px;
  max-width: 16px;
  min-height: 16px;
  max-height: 16px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  margin-right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  transition: all 0.15s ease;
  box-sizing: border-box;

  &.checked {
    background-color: #28a745;
    border-color: #28a745;
  }
`;

const CheckMark = styled.span`
  color: #ffffff;
  font-size: 11px;
  font-weight: bold;
  line-height: 1;
`;
